// DefaultFooter.tsx

import React from "react";
import { useTranslation } from "react-i18next";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { Container, Grid, Box, Typography, useTheme, alpha, Avatar } from "@mui/material";

interface FooterLinkItem {
  labelKey: string;
  path?: string;
  onClick?: () => void;
}

const PLATFORM_LINKS: FooterLinkItem[] = [
  { labelKey: "navbars.defaultFooter.revisionEngine", path: "/revision-engine" },
  { labelKey: "navbars.defaultFooter.termsAndConditions", path: "/tnc" },
];

const RESOURCE_LINKS: FooterLinkItem[] = [
  { labelKey: "navbars.defaultFooter.questionBank", path: "/question-bank" },
  { labelKey: "navbars.defaultFooter.pastPapers", path: "/past-papers" },
  { labelKey: "navbars.defaultFooter.studyTechniques", path: "/study-techniques" },
];

function DefaultFooter() {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const handleLinkClick = (e: React.MouseEvent, path?: string) => {
    e.preventDefault();
    if (path) {
      navigate(path);
    }
  };

  const renderLinkList = (items: FooterLinkItem[]) => (
    <Box
      component="ul"
      sx={{
        p: 0,
        m: 0,
        listStyle: "none",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      {items.map((item) => (
        <Box component="li" key={item.labelKey}>
          <Typography
            component="a"
            href={item.path || "#"}
            variant="body2"
            onClick={(e) => handleLinkClick(e, item.path)}
            sx={{
              color: "text.secondary",
              textDecoration: "none",
              cursor: "pointer",
              transition: "color 0.15s ease-in-out",
              outline: "none",
              "&:hover": { color: "primary.main" },
              "&:focus-visible": {
                color: "primary.main",
                textDecoration: "underline",
              },
            }}
          >
            {t(item.labelKey)}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: isDark ? "background.default" : alpha(theme.palette.background.default, 0.4),
        borderTop: `1px solid ${theme.palette.divider}`,
        pt: 10,
        pb: 8,
        color: "text.primary",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 6 }}>
          {/* Brand Column */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 2.5,
              }}
            >
              <Avatar
                src="/logo.png"
                alt="ComplianceQuizHK"
                sx={{
                  width: 44,
                  height: 44,
                }}
              />
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "-0.03em" }}>
                {t("navbars.defaultFooter.brandName", "Compliance Quiz HK")}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                maxWidth: 280,
                lineHeight: 1.6,
              }}
            >
              {t("navbars.defaultFooter.brandDescription")}
            </Typography>
          </Grid>

          {/* Platform Links */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2.5,
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            >
              {t("navbars.defaultFooter.platformHeader")}
            </Typography>
            {renderLinkList(PLATFORM_LINKS)}
          </Grid>

          {/* Resources Links */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2.5,
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            >
              {t("navbars.defaultFooter.resourcesHeader")}
            </Typography>
            {renderLinkList(RESOURCE_LINKS)}
          </Grid>

          {/* Location Context & Copyright Column */}
          <Grid
            size={{ xs: 12, sm: 4, md: 4 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
                {t("navbars.defaultFooter.madeForHongKong")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  lineHeight: 1.6,
                }}
              >
                {t("navbars.defaultFooter.disclaimer")}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t("navbars.defaultFooter.copyright", { year: new Date().getFullYear() })}
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default DefaultFooter;
